// Publication pages: repair what Ghost's importer strips (classes on <dl> and button links),
// infer the publication type from the citation when no internal tag says so,
// lift the source link into the record, and offer a copy-citation button.
export default function publicationPage() {
    const article = document.querySelector("article.tag-publications, article.tag-reading");
    if (!article) return;
    const isBook = article.classList.contains("tag-reading");
    const content = article.querySelector(".gh-content");

    // 1. Any bare <dl> in the body becomes the key-value grid
    content?.querySelectorAll("dl:not([class])").forEach((dl) => dl.classList.add("gh-kv"));

    // 1a. Tables: two-column tables get a bold label column; five or more columns go wide
    content?.querySelectorAll("table").forEach((t) => {
        const cols = t.tHead?.rows[0]?.cells.length || t.rows[0]?.cells.length || 0;
        if (cols === 2) {
            t.querySelectorAll("tbody tr").forEach((tr) => {
                const td = tr.cells[0];
                if (!td || td.tagName === "TH") return;
                const th = document.createElement("th");
                th.setAttribute("scope", "row");
                th.innerHTML = td.innerHTML;
                td.replaceWith(th);
            });
        }
        if (cols >= 5) {
            const wrap = t.closest(".gh-table-wrap");
            if (wrap) wrap.classList.add("gh-table-wide");
        }
    });

    // 1d. Title with a parenthetical becomes title plus subhead: "Feijão Dam B-1 (Brazil, 2019)"
    const h1 = article.querySelector(".gh-title-pub");
    const tm = h1?.textContent.trim().match(/^(.*\S)\s*\(([^()]+)\)$/);
    if (h1 && tm) {
        h1.textContent = tm[1];
        const sub = document.createElement("p");
        sub.className = "gh-pub-sub";
        sub.textContent = tm[2];
        h1.after(sub);
    }

    // 1b. Ghost's importer flattens a <dl> into one paragraph. Rebuild it from the known labels.
    const LABELS = ["Location", "Type", "Height", "Built", "Constructed", "Failed", "Mode", "Fatalities", "Injuries", "Damage",
        "Presenter", "Presented", "Format", "Host", "Authors", "Author", "Venue", "Where and when", "Journal", "Volume and pages",
        "Published", "Publisher", "DOI", "Pages", "Editor", "Series", "ISBN", "Length"];
    const labelRe = new RegExp("(?=(?:" + LABELS.join("|") + ")(?=[A-Z0-9$€£(\\d]))", "g");
    content?.querySelectorAll("h2 + p").forEach((p) => {
        if (p.children.length) return;
        const text = p.textContent.trim();
        if (!/^(Location|Presenter|Authors?|Venue|Journal)/.test(text)) return;
        const parts = text.split(labelRe).filter(Boolean);
        if (parts.length < 3) return;
        const dl = document.createElement("dl");
        dl.className = "gh-kv gh-kv-rebuilt";
        let ok = 0;
        parts.forEach((part) => {
            const label = LABELS.find((l) => part.startsWith(l));
            if (!label) return;
            const value = part.slice(label.length).trim();
            if (!value) return;
            const dt = document.createElement("dt"); dt.textContent = label;
            const dd = document.createElement("dd"); dd.textContent = value;
            dl.append(dt, dd); ok++;
        });
        if (ok >= 3) p.replaceWith(dl);
    });

    // 1c. Dateline under the title: place and date from the key facts
    const facts = {};
    content?.querySelectorAll("dl dt, table th[scope=row]").forEach((k) => {
        const v = k.tagName === "DT" ? k.nextElementSibling : k.nextElementSibling;
        if (v) facts[k.textContent.trim().toLowerCase()] = v.textContent.trim();
    });
    const dateline = article.querySelector("[data-pub-dateline]");
    if (dateline) {
        const place = facts["location"] || facts["where and when"] || facts["venue"] || "";
        const when = facts["failed"] ? `Failed ${facts["failed"]}` : facts["published"] || facts["presented"] || "";
        const bits = [place, when].filter(Boolean);
        // "Where and when" already carries its date
        const text = facts["where and when"] && !facts["location"] ? facts["where and when"] : bits.join(" \u00b7 ");
        if (text) {
            dateline.innerHTML = text.split(" \u00b7 ").map((t) => `<span>${t}</span>`).join('<span class="gh-sep">&middot;</span>');
            dateline.hidden = false;
        }
    }

    // 1e. Icons: section headings by subject, dateline, source button
    const sprite = document.body.dataset.icons;
    const icon = (name) => sprite ? `<svg class="ci" aria-hidden="true"><use href="${sprite}#${name}"/></svg>` : "";
    const dl = article.querySelector("[data-pub-dateline]");
    if (dl && !dl.hidden) {
        const spans = dl.querySelectorAll("span:not(.gh-sep)");
        if (spans[0]) spans[0].insertAdjacentHTML("afterbegin", icon("pin"));
        if (spans[1]) spans[1].insertAdjacentHTML("afterbegin", icon("calendar"));
    }

    // 2. A paragraph that is only a link becomes the source button, and feeds the record
    let source = null;
    content?.querySelectorAll("p").forEach((p) => {
        const a = p.querySelector("a");
        if (a && p.textContent.trim() === a.textContent.trim() && /^https?:/.test(a.href)) {
            p.classList.add("gh-pub-source");
            a.classList.add("gh-button", "gh-button-primary");
            a.setAttribute("rel", "noopener");
            a.insertAdjacentHTML("afterbegin", icon(/\.pdf(\?|$)/i.test(a.href) ? "download" : (/bookshop\.org/.test(a.href) ? "book" : "arrow-up-right")));
            if (!source) source = a;
        }
    });
    if (isBook) {
        // Books need only the shop link styled as a button; skip the publication record work
        const head = article.querySelector(".gh-book-meta");
        const facts = article.querySelector(".gh-book-facts");
        if (head && facts) head.querySelector(".gh-book-byline")?.after(facts);
        const btn = article.querySelector(".gh-pub-source .gh-button");
        if (head && btn) head.appendChild(btn.closest("p"));
        return;
    }
    const slot = article.querySelector("[data-pub-source]");
    if (slot && source) {
        let host = "";
        try { host = new URL(source.href).hostname.replace(/^www\./, ""); } catch (e) {}
        slot.innerHTML = `<a href="${source.href}" rel="noopener">${host || "Link"}</a>`;
    } else if (slot) {
        slot.innerHTML = "";
    }

    // 3. Type from the citation line when the template found no internal tag
    const typeEl = article.querySelector("[data-pub-type]");
    const cite = article.querySelector("[data-pub-citation]")?.textContent || "";
    if (typeEl && !typeEl.textContent.trim()) {
        const t = /case study/i.test(cite) ? "Case study"
            : /lesson/i.test(cite) ? "Lessons learned"
            : /presentation|slides|talk/i.test(cite) ? "Presentation"
            : /conference|proceedings|symposium/i.test(cite) ? "Conference paper"
            : /journal|jawra|\d+\(\d+\)/i.test(cite) ? "Journal article"
            : /report/i.test(cite) ? "Report" : "";
        typeEl.textContent = t;
        if (!t) typeEl.remove();
    }

    // 4. Co-authors from a "With ..." citation
    const co = article.querySelector("[data-pub-coauthors]");
    const m = cite.match(/^with ([^.]+)\./i);
    if (co && m) co.textContent = `, with ${m[1].replace(/ and /g, ", ").replace(/,\s*,/g, ",")}`;

    // 5. Copy citation
    const btn = article.querySelector("[data-copy-cite]");
    const text = article.querySelector("[data-cite]")?.textContent.replace(/\s+/g, " ").trim();
    if (btn && text && navigator.clipboard) {
        btn.addEventListener("click", async () => {
            try {
                await navigator.clipboard.writeText(text);
                const label = btn.querySelector("span") || btn;
                const prev = label.innerHTML;
                label.textContent = "Copied";
                setTimeout(() => (label.innerHTML = prev), 1600);
            } catch (e) {}
        });
    } else if (btn) {
        btn.remove();
    }
}
